import { useEffect, useMemo, useState, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import UptimeDashboardDriftRow from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardDriftRow"
import UptimeDashboardDownRow from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardDownRow"
import UptimeDashboardHeader from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardHeader"
import UptimeDashboardKpis from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardKpis"
import UptimeDashboardLane from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardLane"
import UptimeDashboardSlowRow from "@/v2-backoffice-client/uptime/dashboard/UptimeDashboardSlowRow"
import { useDashboardDriftSummariesQuery } from "@/v2-backoffice-client/uptime/dashboard/dashboard.driftSummaries.query"
import { useUptimeQuery } from "@/v2-backoffice-client/uptime/uptime.query"
import { useUptimeStatusConfigBootstrap } from "@/v2-backoffice-client/uptime/uptime.statusConfigBootstrap"
import {
  computeOverallStatus,
  dashboardTargetKey,
  splitTargets,
} from "@/v2-backoffice-client/uptime/dashboard/uptime.dashboard.utils"
import type { DashboardDriftByKey } from "@/v2-backoffice-client/uptime/dashboard/uptime.dashboard.types"
import type { LayerDriftSummaryFragment } from "@/v2-backoffice-client/uptime/dashboard/__types__/dashboard.driftSummaries.fragment"

export type UptimeDashboardViewProps = ComponentProps<"section"> & {
  onExit(): void
}

// Polling cadence for both the uptime list and the drift severity
// rollup. Tighter than the regular page (60 s) because the dashboard
// is meant to be the source of truth for "what's happening right
// now" on the office wall.
const POLL_INTERVAL_MS = 15_000

// UptimeDashboardView is the read-only "wall display" mode of the
// uptime page. It is meant to live on a 4K-ish office monitor in
// always-on view, so it:
//
//   - hides every interactive control (no clicks, no scroll-to)
//   - prefers giant, color-saturated headlines that can be read
//     across the room
//   - polls more aggressively than the regular board (every 15 s)
//     and surfaces a "live" pip + a "checked Xs ago" timestamp so
//     passers-by can tell the data is fresh
//   - splits the screen into three big lanes (incidents, slow,
//     drift) that stay visible side by side instead of stacking,
//     because at this resolution there's room
//   - flashes attention-grabbing color when the worst severity
//     escalates so the room notices the moment a service goes down
//   - displays *every* matching row in each lane (no "...and N
//     more" cap); the lane bodies scroll when the list is long
//
// This file is intentionally a thin orchestrator: data fetching,
// keyboard handling, and lifecycle ticks live here, but every
// piece of UI lives in its own sibling file.
function UptimeDashboardView(props: UptimeDashboardViewProps) {
  const { onExit, className, ...sectionProps } = props

  // Populate the module-level status-config cache so the helper
  // labels (Category / Provider / Platform) match operator edits.
  useUptimeStatusConfigBootstrap()

  const { data, previousData, loading } = useUptimeQuery({
    pollInterval: POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: true,
  })
  // Per-target drift severity rollup (breaking/warning/info counts)
  // is too expensive to inline into the uptime list endpoint, so we
  // pull it in a separate small query polled on the same cadence.
  const { data: driftData, previousData: driftPrev } =
    useDashboardDriftSummariesQuery({
      pollInterval: POLL_INTERVAL_MS,
      notifyOnNetworkStatusChange: true,
    })

  const display = data ?? previousData
  const overview = display?.getUptimeOverview ?? null
  const targets = display?.getUptimeTargets ?? []
  const driftSummaries =
    driftData?.getLayerDriftSummaries ?? driftPrev?.getLayerDriftSummaries ?? []

  // Track the timestamp of the last network response so the header
  // can display "checked Ns ago" instead of relying on a server-side
  // "as of" field. We bump it whenever a fresh `data` reference
  // arrives.
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)
  useEffect(() => {
    if (data) setLastUpdatedAt(new Date())
  }, [data])

  // Esc closes the dashboard so kiosk users (or whoever is at the
  // keyboard) have a way out without hunting for a button.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onExit()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onExit])

  // Index drift summaries by Source|Identifier so DriftRow can pull
  // the severity rollup for the target it renders without scanning
  // the list each time. Muted targets are excluded so the severity
  // sums on the KPI tile (sumDriftSeverity) stay consistent with the
  // drift lane and KPI count -- both of which already skip muted.
  const mutedKeys = useMemo(() => {
    const set = new Set<string>()
    for (const t of targets) {
      if (t.IsMuted) set.add(`${t.Source}|${t.Identifier}`)
    }
    return set
  }, [targets])
  const driftByKey = useMemo<DashboardDriftByKey>(() => {
    const map = new Map<string, LayerDriftSummaryFragment>()
    for (const s of driftSummaries) {
      if (!s.HasDrift) continue
      const key = `${s.Source}|${s.Identifier}`
      if (mutedKeys.has(key)) continue
      map.set(key, s)
    }
    return map
  }, [driftSummaries, mutedKeys])

  const split = useMemo(
    () => splitTargets(targets, driftByKey),
    [targets, driftByKey],
  )
  const status = computeOverallStatus(overview, split.drift.length)

  return (
    <section
      className={cn(
        "uptime-dashboard",
        `uptime-dashboard-status-${status.level}`,
        className,
      )}
      aria-label="Uptime dashboard"
      {...sectionProps}
    >
      <UptimeDashboardHeader
        status={status}
        loading={loading}
        lastUpdatedAt={lastUpdatedAt}
        onExit={onExit}
      />

      <UptimeDashboardKpis
        overview={overview}
        targets={targets}
        driftCount={split.drift.length}
        driftByKey={driftByKey}
      />

      <div className="uptime-dashboard-grid">
        <UptimeDashboardLane
          title="Down"
          icon="triangle-warning"
          tone="critical"
          emptyText="No services are down"
          totalCount={split.down.length}
        >
          {split.down.map((t) => (
            <UptimeDashboardDownRow key={dashboardTargetKey(t)} target={t} />
          ))}
        </UptimeDashboardLane>

        <UptimeDashboardLane
          title="Slow"
          icon="signal-alt-1"
          tone="warning"
          emptyText="No latency outliers"
          totalCount={split.slow.length}
        >
          {split.slow.map((t) => (
            <UptimeDashboardSlowRow key={dashboardTargetKey(t)} target={t} />
          ))}
        </UptimeDashboardLane>

        <UptimeDashboardLane
          title="Configuration drift"
          icon="swap"
          tone="drift"
          emptyText="No drifting layers"
          totalCount={split.drift.length}
        >
          {split.drift.map((t) => (
            <UptimeDashboardDriftRow
              key={dashboardTargetKey(t)}
              target={t}
              summary={driftByKey.get(dashboardTargetKey(t)) ?? null}
            />
          ))}
        </UptimeDashboardLane>
      </div>

      <footer className="uptime-dashboard-footer">
        <span>Press</span>
        <kbd className="uptime-dashboard-kbd">Esc</kbd>
        <span>to exit</span>
        <span className="uptime-dashboard-footer-spacer" />
        <span>Auto-refreshing every 15 s</span>
      </footer>
    </section>
  )
}

export default UptimeDashboardView
